import i18n, { i18nNS } from '../../i18n';
import { omit } from '../../utils/objects';
import request from '../request';
import { getIsIntroductionSkipped, setSkipIntroduction } from '../shared/preferences';
import { DialogTypes } from '../shared/types';
import { ClassesAPI } from './api-types';
import { AttendanceData, ClassListItem } from './types';
import urls from './urls';

export async function getValidClasses(
  payload: ClassesAPI.GetValidClassesRequest
): Promise<ClassesAPI.GetValidClassesSuccessBaseTransformedResponse> {
  const { data } = await request.get<ClassesAPI.GetValidClassesResponse>(urls.getValidClasses, {
    headers: { courseId: payload.courseId },
  });

  const { message } = data;
  if (message !== 'success') throw new Error(i18n.t('unable_to_fetch_classes', { ns: i18nNS.POLL }));

  const { classes } = data;

  const validClasses: ClassListItem[] = classes.map((cls) => ({
    ...omit(cls, 'isInCharge'),
    isIncharge: cls.isInCharge === 1,
  }));

  return { classes: validClasses };
}

export async function getValidAttendanceData(): Promise<ClassesAPI.GetValidAttendanceDataTransformedResponse> {
  const { data: apiData } = await request.get<ClassesAPI.getValidAttendanceDataResponse>(
    urls.getValidAttendanceData
  );

  const data: AttendanceData = {
    inSession: apiData.inSession.map((data) => {
      const { courseData, classData } = data;
      return {
        courseData,
        classData: classData.map((cls) => {
          return { ...omit(cls, 'inCharge'), isIncharge: cls.inCharge === 1 };
        }),
      };
    }),
    nextScheduled: apiData.nextScheduled.map((data) => {
      const { courseData, classData } = data;
      return {
        courseData,
        classData: classData.map((cls) => {
          return { ...omit(cls, 'inCharge'), isIncharge: cls.inCharge === 1 };
        }),
      };
    }),
  };

  return data;
}

export async function startProxyAttendance(
  payload: ClassesAPI.StartProxyAttendanceRequest
): Promise<ClassesAPI.StartProxyAttendanceResponse> {
  const { data } = await request.post<ClassesAPI.StartProxyAttendanceResponse>(
    urls.startProxyAttendance,
    payload
  );
  return data;
}

export async function stopProxyAttendance({
  courseId,
  ...payload
}: ClassesAPI.StopProxyAttendanceRequest): Promise<void> {
  await request.post(urls.stopProxyAttendance, payload, {
    headers: { courseId },
  });
}

export async function getAttendanceResponders(
  payload: ClassesAPI.GetAttendanceRespondersRequest
): Promise<ClassesAPI.GetAttendanceRespondersTransformedResponse> {
  const { courseId, classId } = payload;
  const { data, status } = await request.get<ClassesAPI.GetAttendanceRespondersResponse>(
    urls.getAttendanceResponders,
    {
      headers: { courseId },
      params: { classId },
      validateStatus: (status) => status >= 200 && status < 500,
    }
  );

  if (status >= 200 && status < 300) {
    if (data.message !== 'success') return undefined;
  } else {
    return undefined;
  }

  return data;
}

export function setAttendanceSkipIntroduction(value: boolean) {
  setSkipIntroduction(DialogTypes.ATTENDANCE, value);
}

export function getIsAttendanceIntroductionSkipped() {
  return getIsIntroductionSkipped(DialogTypes.ATTENDANCE);
}
